import type { Client, Project, Settings, Task, TimeEntry } from "./entities";
import { addMoney, type Currency, type Money, type MoneyByCurrency } from "./money";
import { isLive, type Dataset } from "./operations";

const HOUR = 3_600_000;

/** Project Currency, resolved Project → Client → Default Currency. */
export function resolveCurrency(project: Project, client: Client | undefined, settings: Settings): Currency {
  return project.currency ?? client?.defaultCurrency ?? settings.defaultCurrency;
}

/**
 * Hourly Rate, resolved Project → Client → global default. A Client or global rate only applies
 * when it is expressed in the Project's currency; ontrack never converts between currencies.
 */
export function resolveHourlyRate(project: Project, client: Client | undefined, settings: Settings): Money | null {
  const currency = resolveCurrency(project, client, settings);
  if (project.hourlyRate !== null) return { amount: project.hourlyRate, currency };
  if (client && client.defaultHourlyRate !== null && (client.defaultCurrency ?? settings.defaultCurrency) === currency) {
    return { amount: client.defaultHourlyRate, currency };
  }
  if (settings.defaultHourlyRate !== null && settings.defaultCurrency === currency) {
    return { amount: settings.defaultHourlyRate, currency };
  }
  return null;
}

/** Billable defaults from the Billing Mode. */
export function defaultBillable(project: Project | undefined): boolean {
  return project ? project.billingMode !== "non-billable" : false;
}

/** Whether an entry may be marked Billable. Entries in Non-billable Projects never can. */
export function canBeBillable(project: Project | undefined): boolean {
  return project !== undefined && project.billingMode !== "non-billable";
}

/** The Rate Snapshot to store on an entry being recorded now. Only Hourly, Billable entries get one. */
export function rateSnapshotFor(dataset: Dataset, projectId: string | null, billable: boolean): Money | null {
  if (!projectId || !billable) return null;
  const project = dataset.projects[projectId];
  if (!project || project.billingMode !== "hourly") return null;
  const client = project.clientId ? dataset.clients[project.clientId] : undefined;
  return resolveHourlyRate(project, client, dataset.settings);
}

export function entryDuration(entry: Pick<TimeEntry, "start" | "end">, now: number): number {
  return Math.max(0, (entry.end ?? now) - entry.start);
}

/** Money an entry has earned so far, or null when it earns nothing by the hour. */
export function entryEarnings(entry: TimeEntry, now: number): Money | null {
  if (!entry.billable || !entry.rateSnapshot) return null;
  return { amount: Math.round(entry.rateSnapshot.amount * entryDuration(entry, now) / HOUR), currency: entry.rateSnapshot.currency };
}

export function projectCurrency(dataset: Dataset, project: Project): Currency {
  return resolveCurrency(project, project.clientId ? dataset.clients[project.clientId] : undefined, dataset.settings);
}

export interface Range { from: number; to: number }

/**
 * Earned money per currency in a range: Billable hourly time started in the range, plus Fixed
 * Price items completed in it (a done Task, or a completed Project for a Project-level price).
 */
export function earnedByCurrency(dataset: Dataset, range: Range, now: number): MoneyByCurrency {
  let totals: MoneyByCurrency = {};
  for (const entry of Object.values(dataset.timeEntries)) {
    if (!isLive(entry) || entry.start < range.from || entry.start >= range.to) continue;
    const earned = entryEarnings(entry, now);
    if (earned) totals = addMoney(totals, earned);
  }
  for (const task of Object.values(dataset.tasks)) {
    totals = addFixed(dataset, totals, task, range);
  }
  for (const project of Object.values(dataset.projects)) {
    if (!isLive(project) || project.billingMode !== "fixed" || project.fixedPrice === null || project.completedAt === null) continue;
    if (project.completedAt < range.from || project.completedAt >= range.to) continue;
    totals = addMoney(totals, { amount: project.fixedPrice, currency: projectCurrency(dataset, project) });
  }
  return totals;
}

function addFixed(dataset: Dataset, totals: MoneyByCurrency, task: Task, range: Range): MoneyByCurrency {
  if (!isLive(task) || task.fixedPrice === null || task.doneAt === null) return totals;
  if (task.doneAt < range.from || task.doneAt >= range.to) return totals;
  const project = dataset.projects[task.projectId];
  if (!project || !isLive(project) || project.billingMode !== "fixed") return totals;
  return addMoney(totals, { amount: task.fixedPrice, currency: projectCurrency(dataset, project) });
}

/** Total tracked and billable milliseconds for entries started in a range. */
export function trackedTime(dataset: Dataset, range: Range, now: number): { total: number; billable: number; entries: number } {
  let total = 0, billable = 0, entries = 0;
  for (const entry of Object.values(dataset.timeEntries)) {
    if (!isLive(entry) || entry.start < range.from || entry.start >= range.to) continue;
    const duration = entryDuration(entry, now);
    total += duration;
    if (entry.billable) billable += duration;
    entries += 1;
  }
  return { total, billable, entries };
}

export interface ProjectTotals {
  tracked: number;
  billable: number;
  /** Hourly earnings from entries plus completed Fixed Prices, in the Project's currency. */
  earned: number;
  currency: Currency;
  /** Earned per tracked hour, in minor units; null when nothing is tracked. */
  effectiveHourlyRate: number | null;
}

/** Totals for one Project, optionally limited to entries started in a range. */
export function projectTotals(dataset: Dataset, projectId: string, now: number, range?: Range): ProjectTotals {
  const project = dataset.projects[projectId];
  const currency = project ? projectCurrency(dataset, project) : dataset.settings.defaultCurrency;
  let tracked = 0, billable = 0, earned = 0;
  for (const entry of Object.values(dataset.timeEntries)) {
    if (!isLive(entry) || entry.projectId !== projectId) continue;
    if (range && (entry.start < range.from || entry.start >= range.to)) continue;
    const duration = entryDuration(entry, now);
    tracked += duration;
    if (entry.billable) billable += duration;
    const value = entryEarnings(entry, now);
    if (value && value.currency === currency) earned += value.amount;
  }
  if (project?.billingMode === "fixed") {
    const inRange = (ts: number | null) => ts !== null && (!range || (ts >= range.from && ts < range.to));
    for (const task of Object.values(dataset.tasks)) {
      if (isLive(task) && task.projectId === projectId && task.fixedPrice !== null && inRange(task.doneAt)) earned += task.fixedPrice;
    }
    if (project.fixedPrice !== null && inRange(project.completedAt)) earned += project.fixedPrice;
  }
  return { tracked, billable, earned, currency, effectiveHourlyRate: tracked > 0 ? Math.round(earned / (tracked / HOUR)) : null };
}
